import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Camera, 
  Database, 
  RefreshCw, 
  Search, 
  UserPlus, 
  User, 
  Calendar,
  ChevronRight,
  MoreHorizontal,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { patients as patientService, medicalDocuments } from '../services/api';
import CameraCapture from '../components/CameraCapture';
import DocumentUploadForm from '../components/DocumentUploadForm';
import ImageSourceModal from '../components/ImageSourceModal';
import { useTranslation } from 'react-i18next';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";

interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  created_at: string;
}

const PatientList: React.FC = () => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for document scanning and upload
  const [showImageSourceModal, setShowImageSourceModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [capturedImage, setCapturedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [newPatientId, setNewPatientId] = useState<string | null>(null);
  const [isConnectingHIS, setIsConnectingHIS] = useState(false);

  // Function to fetch patients
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await patientService.getAll();
      setPatients(data);
    } catch (error: any) {
      console.error('Error fetching patients:', error);
      setError(error.response?.data?.detail || t('common.loadPatientsFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  useEffect(() => {
    if (newPatientId) {
      const timer = setTimeout(() => {
        setNewPatientId(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [newPatientId]);

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Function to translate gender
  const translateGender = (gender: string) => {
    return gender === 'male' 
      ? t('patientDetail.genderOptions.male') || '男' 
      : gender === 'female' 
        ? t('patientDetail.genderOptions.female') || '女' 
        : t('common.other') || '其他';
  };
  
  // Handle camera capture
  const handleCameraCapture = (file: File) => {
    setCapturedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target && typeof e.target.result === 'string') {
        setImagePreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
    setShowCameraModal(false);
    setShowUploadForm(true);
  };

  // Cancel upload process
  const handleCancelUpload = () => {
    setCapturedImage(null);
    setImagePreview(null);
    setShowCameraModal(false);
    setShowImageSourceModal(false);
    setShowUploadForm(false);
  };

  // Handle document upload to create a new patient
  const handleDocumentUpload = async (documentType: string, notes: string) => {
    if (!capturedImage) {
      setError(t('common.noImageSelected'));
      setShowUploadForm(false);
      return;
    }

    try {
      setError('');
      setSuccessMessage(null);
      const response = await medicalDocuments.scanNewPatient(capturedImage, notes);
      
      if (response.success) {
        setCapturedImage(null);
        setImagePreview(null);
        setShowUploadForm(false);
        setSuccessMessage( t('patientList.patientCreated') );
        setNewPatientId(response.patient_id);
        await fetchPatients();
      } else {
        setError(t('common.createPatientFailed'));
      }
    } catch (error: any) {
      console.error('Error creating new patient from document:', error);
      setError(error.message || t('common.createPatientFailed'));
    }
  };

  // Handle HIS connection
  const handleHISConnect = () => {
    setIsConnectingHIS(true);
    setTimeout(() => {
      setIsConnectingHIS(false);
      setSuccessMessage(t('patientList.hisConnected'));
      fetchPatients();
    }, 3000);
  };

  const filteredPatients = patients.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <AnimatePresence>
        {successMessage && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center justify-between shadow-sm"
          >
            <div className="flex items-center">
              <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
              <span>{successMessage}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSuccessMessage(null)}>
              {t('common.close')}
            </Button>
          </motion.div>
        )}
        
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl flex items-center shadow-sm"
          >
            <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('patientList.title')}</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => setShowImageSourceModal(true)}
            className="rounded-xl border-gray-200 shadow-sm hover:bg-gray-50"
          >
            <Camera className="h-4 w-4 mr-2 text-primary" />
            {t('patientList.scanDocument')}
          </Button>
          <Button
            asChild
            className="rounded-xl shadow-sm bg-primary hover:bg-primary/90"
          >
            <Link to="/patient/new">
              <Plus className="h-4 w-4 mr-2" />
              {t('patientList.manualCreate')}
            </Link>
          </Button>
          <Button
            onClick={handleHISConnect}
            disabled={isConnectingHIS}
            variant="secondary"
            className="rounded-xl shadow-sm"
          >
            {isConnectingHIS ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Database className="h-4 w-4 mr-2" />
            )}
            {isConnectingHIS ? t('patientList.connecting') : t('patientList.importFromHIS')}
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl border-gray-200/60 shadow-sm overflow-hidden bg-white/50 backdrop-blur-sm">
        <CardHeader className="pb-3 border-b border-gray-100 bg-gray-50/30">
          <div className="flex items-center justify-between">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder={t('common.search') || 'Search patients...'} 
                className="pl-9 rounded-xl border-gray-200 bg-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64 gap-3">
              <RefreshCw className="h-8 w-8 animate-spin text-primary/60" />
              <p className="text-sm text-muted-foreground animate-pulse">{t('common.loading')}</p>
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="h-16 w-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <UserPlus className="h-8 w-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">{t('patientList.noPatients')}</h3>
              <p className="text-sm text-muted-foreground max-w-xs mt-1">
                {t('patientList.noPatientsDesc') || 'Start by adding a new patient manually or scanning a medical document.'}
              </p>
              <Button variant="outline" className="mt-6 rounded-xl" asChild>
                <Link to="/patient/new">
                   {t('patientList.manualCreate')}
                </Link>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-50/50">
                  <TableRow className="hover:bg-transparent border-gray-100">
                    <TableHead className="font-semibold py-4">{t('patientDetail.fields.name') || 'Name'}</TableHead>
                    <TableHead className="font-semibold">{t('patientDetail.fields.age') || 'Age'}</TableHead>
                    <TableHead className="font-semibold">{t('patientDetail.fields.gender') || 'Gender'}</TableHead>
                    <TableHead className="font-semibold">{t('patientList.createdAt')}</TableHead>
                    <TableHead className="text-right pr-6 font-semibold">{t('patientList.actions') || 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPatients.map((patient, index) => (
                    <motion.tr
                      key={patient.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`group hover:bg-gray-50/80 transition-colors border-gray-100 cursor-pointer ${newPatientId === patient.id ? "bg-primary/5 animate-pulse" : ""}`}
                      onClick={() => window.location.href = `/patient/${patient.id}`}
                    >
                      <TableCell className="py-4">
                        <div className="flex items-center">
                          <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center mr-3 group-hover:bg-primary group-hover:text-white transition-colors">
                            <User className="h-5 w-5 text-primary group-hover:text-white transition-colors" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 group-hover:text-primary transition-colors">{patient.name}</div>
                            <div className="text-xs text-muted-foreground">ID: {patient.id.substring(0, 8)}...</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 font-medium">{patient.age}</span>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary"
                          className={
                            patient.gender === 'male' 
                              ? 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100' 
                              : 'bg-pink-50 text-pink-700 border-pink-100 hover:bg-pink-100'
                          }
                        >
                          {translateGender(patient.gender)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground flex items-center pt-6">
                        <Calendar className="h-3.5 w-3.5 mr-2 opacity-60" />
                        {formatDate(patient.created_at)}
                      </TableCell>
                      <TableCell className="text-right pr-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="icon" className="rounded-lg h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="rounded-lg h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-xl">
                              <DropdownMenuItem asChild>
                                <Link to={`/patient/${patient.id}`} className="cursor-pointer">
                                  {t('common.view') || 'View Details'}
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive focus:text-destructive cursor-pointer">
                                {t('common.delete') || 'Delete'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image source selection modal */}
      <ImageSourceModal
        isOpen={showImageSourceModal}
        onClose={handleCancelUpload}
        onCameraSelect={() => {
          setShowImageSourceModal(false);
          setShowCameraModal(true);
        }}
        onFileSelect={(file) => {
          setCapturedImage(file);
          const reader = new FileReader();
          reader.onload = (e) => {
            if (e.target && typeof e.target.result === 'string') {
              setImagePreview(e.target.result);
            }
          };
          reader.readAsDataURL(file);
          setShowImageSourceModal(false);
          setShowUploadForm(true);
        }}
      />

      {/* Camera capture modal */}
      {showCameraModal && (
        <CameraCapture 
          onCapture={handleCameraCapture}
          onClose={handleCancelUpload}
        />
      )}

      {/* Document upload form */}
      {showUploadForm && capturedImage && imagePreview && (
        <DocumentUploadForm
          imageFile={capturedImage}
          imagePreview={imagePreview}
          onSubmit={handleDocumentUpload}
          onCancel={handleCancelUpload}
          predefinedType="MEDICAL_RECORD"
        />
      )}
    </div>
  );
};

export default PatientList;
